// src/pages/ActiveRide.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { updateRideStatus, listenToMyRide } from '../services/firebase';
import { useRider } from '../context/RiderContext';

const PHASES = {
  ARRIVING: 'arriving',       // driving to pickup
  ARRIVED: 'arrived',         // at pickup, waiting for OTP
  STARTED: 'started',         // trip in progress
  COMPLETED: 'completed',     // done
};

export default function ActiveRide() {
  const navigate = useNavigate();
  const { currentRide, setCurrentRide, rider } = useRider();
  const [phase, setPhase] = useState(PHASES.ARRIVING);
  const [enteredOtp, setEnteredOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [rideData, setRideData] = useState(currentRide);
  const timerRef = useRef(null);
  const rideListener = useRef(null);

  useEffect(() => {
    if (!currentRide?.id) { navigate('/'); return; }
    rideListener.current = listenToMyRide(currentRide.id, (data) => {
      setRideData(data);
      // Customer cancelled — go back to dashboard
      if (data.status === 'cancelled') {
        toast('❌ Customer cancelled the ride', { icon: '⚠️' });
        setCurrentRide(null);
        navigate('/');
      }
    });
    return () => { rideListener.current?.(); clearInterval(timerRef.current); };
  }, []);

  // Start elapsed timer when trip starts
  useEffect(() => {
    if (phase === PHASES.STARTED) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
  }, [phase]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleArrivedAtPickup = async () => {
    setLoading(true);
    try {
      await updateRideStatus(currentRide.id, 'arrived');
      setPhase(PHASES.ARRIVED);
      toast.success('Marked as arrived at pickup!');
    } catch { toast.error('Try again'); }
    finally { setLoading(false); }
  };

  const handleStartTrip = async () => {
    if (enteredOtp !== rideData?.otp) {
      toast.error('Wrong OTP! Ask customer for correct OTP.');
      return;
    }
    setLoading(true);
    try {
      await updateRideStatus(currentRide.id, 'started', { startedAt: new Date().toISOString() });
      setPhase(PHASES.STARTED);
      toast.success('Trip started! 🛺');
    } catch { toast.error('Try again'); }
    finally { setLoading(false); }
  };

  const handleCompleteTrip = async () => {
    setLoading(true);
    try {
      await updateRideStatus(currentRide.id, 'completed', {
        completedAt: new Date().toISOString(),
        durationSecs: elapsed,
      });
      setPhase(PHASES.COMPLETED);
      toast.success('Trip completed! 🎉');
    } catch { toast.error('Try again'); }
    finally { setLoading(false); }
  };

  const handleDone = () => {
    setCurrentRide(null);
    navigate('/');
  };

  const earning = Math.round((rideData?.fare || 0) * 0.85);

  // ── COMPLETED SCREEN ──
  if (phase === PHASES.COMPLETED) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'linear-gradient(145deg, #0a1a12, #0a0a0a)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 72, marginBottom: 16, animation: 'slideUp 0.5s ease' }}>🎉</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--green)', marginBottom: 8 }}>Trip Complete!</div>
        <div style={{ fontSize: 14, color: 'var(--gray)', marginBottom: 28 }}>Great job! Payment collected.</div>

        <div style={{ background: 'var(--card)', borderRadius: 20, padding: 20, width: '100%', marginBottom: 24, border: '1px solid rgba(0,214,143,0.2)' }}>
          <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 4 }}>Your Earning</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: 'var(--green)' }}>₹{earning}</div>
          <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>({rideData?.paymentMode === 'cash' ? 'Collect cash from customer' : 'Online — already paid'})</div>

          <div style={{ height: 1, background: 'var(--border)', margin: '14px 0' }} />
          {[
            { label: '📍 Route', value: `→ ${rideData?.destination?.name}` },
            { label: '📏 Distance', value: rideData?.distance || '—' },
            { label: '⏱ Duration', value: formatTime(elapsed) },
            { label: '💵 Total fare', value: `₹${rideData?.fare}` },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', color: 'var(--text)' }}>
              <span style={{ color: 'var(--gray)' }}>{r.label}</span>
              <span style={{ fontWeight: 700 }}>{r.value}</span>
            </div>
          ))}
        </div>

        <button className="btn btn-green" onClick={handleDone}>Back to Dashboard →</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>

      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-logo">My<span>Townly</span></div>
        <div style={{ marginLeft: 'auto', background: 'rgba(0,214,143,0.1)', color: 'var(--green)', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(0,214,143,0.3)' }}>
          {phase === PHASES.ARRIVING ? '🛺 Go to Pickup' : phase === PHASES.ARRIVED ? '📍 At Pickup' : `🏁 Trip ${formatTime(elapsed)}`}
        </div>
      </div>

      {/* Mini map visual */}
      <div style={{ height: 180, background: 'linear-gradient(160deg, #0d1f15, #111318)', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,214,143,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,214,143,0.06) 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        {/* Animated rider */}
        <div style={{ position: 'absolute', fontSize: 28, top: '40%', animation: phase === PHASES.STARTED ? 'driverMove 4s linear infinite alternate' : 'none', left: phase === PHASES.ARRIVING ? '20%' : '50%', transition: 'left 1s ease' }}>🛺</div>
        {/* Destination pin */}
        <div style={{ position: 'absolute', right: '20%', top: '30%', fontSize: 28 }}>📍</div>
        {/* Route line */}
        <div style={{ position: 'absolute', top: '52%', left: '25%', right: '25%', height: 2, background: 'var(--green)', opacity: 0.4 }} />
        <style>{`@keyframes driverMove { from{left:15%} to{left:65%} }`}</style>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 100px' }}>

        {/* Status banner */}
        <div style={{ background: phase === PHASES.ARRIVED ? 'var(--orange-glow)' : 'var(--green-glow)', border: `1px solid ${phase === PHASES.ARRIVED ? 'rgba(255,107,53,0.4)' : 'rgba(0,214,143,0.3)'}`, borderRadius: 14, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase === PHASES.ARRIVED ? 'var(--orange)' : 'var(--green)', animation: 'blink 1.2s infinite' }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: phase === PHASES.ARRIVED ? 'var(--orange)' : 'var(--green)' }}>
            {phase === PHASES.ARRIVING && 'Head to pickup location'}
            {phase === PHASES.ARRIVED && 'Ask customer for OTP to start trip'}
            {phase === PHASES.STARTED && `Trip in progress — ${formatTime(elapsed)}`}
          </div>
        </div>

        {/* Customer & route info */}
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray)', marginBottom: 10, letterSpacing: 0.5 }}>TRIP DETAILS</div>
          {[
            { label: '👤 Customer', value: rideData?.userId?.slice(0, 8) + '...' || 'Customer' },
            { label: '📍 Pickup', value: rideData?.pickup?.address || '—' },
            { label: '🏁 Drop', value: rideData?.destination?.name || '—' },
            { label: '📏 Distance', value: rideData?.distance || '—' },
            { label: '💰 Your cut', value: `₹${earning} (85%)` },
            { label: '💵 Payment', value: rideData?.paymentMode === 'cash' ? '💵 Collect Cash' : '📱 Online Paid' },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--gray)' }}>{r.label}</span>
              <span style={{ fontWeight: 600, maxWidth: '55%', textAlign: 'right' }}>{r.value}</span>
            </div>
          ))}
        </div>

        {/* OTP Entry (only when arrived) */}
        {phase === PHASES.ARRIVED && (
          <div className="card" style={{ padding: 16, marginBottom: 12, border: '1px solid rgba(255,107,53,0.3)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange)', marginBottom: 10 }}>🔐 Enter Customer OTP</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 12 }}>Ask the customer for their 4-digit OTP to start the trip</div>
            <input
              type="tel" maxLength={4}
              placeholder="Enter 4-digit OTP"
              value={enteredOtp}
              onChange={e => setEnteredOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
              style={{ textAlign: 'center', fontSize: 28, fontWeight: 900, letterSpacing: 8, paddingLeft: 14, marginBottom: 12, border: '2px solid var(--orange)' }}
            />
            <button className="btn" style={{ background: 'var(--orange)', color: 'white' }} onClick={handleStartTrip} disabled={loading || enteredOtp.length < 4}>
              {loading ? <span className="spinner" /> : '▶ Start Trip'}
            </button>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {phase === PHASES.ARRIVING && (
            <button className="btn btn-green" onClick={handleArrivedAtPickup} disabled={loading}>
              {loading ? <span className="spinner" /> : '📍 I\'ve Arrived at Pickup'}
            </button>
          )}
          {phase === PHASES.STARTED && (
            <button className="btn btn-green" onClick={handleCompleteTrip} disabled={loading}>
              {loading ? <span className="spinner" /> : '🏁 Complete Trip'}
            </button>
          )}
          <button className="btn btn-dark" onClick={() => toast('📞 Calling customer...')}>
            📞 Call Customer
          </button>
        </div>
      </div>
    </div>
  );
}
